<script lang="ts">
  import { copyText } from '#lib/platform/clipboard.js';
  import { goto } from '$app/navigation';
  import type { RoomListItemView, RoomTag } from '#src/generated/protocol';
  import ArrowLineUpIcon from 'phosphor-svelte/lib/ArrowLineUpIcon';
  import ArrowUpIcon from 'phosphor-svelte/lib/ArrowUpIcon';
  import ArrowDownIcon from 'phosphor-svelte/lib/ArrowDownIcon';
  import ChatCircleIcon from 'phosphor-svelte/lib/ChatCircleIcon';
  import CircleDashedIcon from 'phosphor-svelte/lib/CircleDashedIcon';
  import ChecksIcon from 'phosphor-svelte/lib/ChecksIcon';
  import DotsThreeVerticalIcon from 'phosphor-svelte/lib/DotsThreeVerticalIcon';
  import EyeIcon from 'phosphor-svelte/lib/EyeIcon';
  import EyeSlashIcon from 'phosphor-svelte/lib/EyeSlashIcon';
  import FingerprintIcon from 'phosphor-svelte/lib/FingerprintIcon';
  import FlagIcon from 'phosphor-svelte/lib/FlagIcon';
  import GearIcon from 'phosphor-svelte/lib/GearIcon';
  import LinkIcon from 'phosphor-svelte/lib/LinkIcon';
  import ListBulletsIcon from 'phosphor-svelte/lib/ListBulletsIcon';
  import PushPinSlashIcon from 'phosphor-svelte/lib/PushPinSlashIcon';
  import SignInIcon from 'phosphor-svelte/lib/SignInIcon';
  import SignOutIcon from 'phosphor-svelte/lib/SignOutIcon';
  import StarIcon from 'phosphor-svelte/lib/StarIcon';
  import UserPlusIcon from 'phosphor-svelte/lib/UserPlusIcon';
  import UsersThreeIcon from 'phosphor-svelte/lib/UsersThreeIcon';

  import RoomInviteDialog from '#lib/features/room/RoomInviteDialog.svelte';
  import RoomNotificationSubmenu from '#lib/features/room/RoomNotificationSubmenu.svelte';
  import { spaceTimelinePath } from '#lib/features/room/event-timeline.js';

  import { useCoreClient } from '#lib/core/context.js';
  import { joinRoom } from '#lib/core/api/rooms.js';
  import { i18n } from '#lib/i18n.js';
  import { copyRoomLink } from '#lib/rooms/permalink.js';
  import { isQuiet, setQuiet } from '#lib/rooms/quiet-rooms.svelte.js';
  import { useRoomList } from '#lib/rooms/room-list.svelte.js';
  import { preferences, readReceiptIsPrivate } from '#lib/settings/preferences.svelte.js';
  import { toasts } from '#lib/ui/toasts.svelte.js';
  import type { CursorAnchor } from '#lib/ui/cursor-anchor.js';
  import ActionMenu from '#lib/ui/primitives/ActionMenu.svelte';
  import ActionMenuItem from '#lib/ui/primitives/ActionMenuItem.svelte';
  import ActionMenuSeparator from '#lib/ui/primitives/ActionMenuSeparator.svelte';
  import { untrack } from 'svelte';
  import { SvelteMap, SvelteSet } from 'svelte/reactivity';

  import IconContext from 'phosphor-svelte/lib/IconContext';

  import AddToSpaceDialog from './AddToSpaceDialog.svelte';
  import { wouldCreateCycle } from './add-to-space.js';
  import { markRoomUnread, markRoomsRead, spaceDescendantRooms } from './nav-rooms.js';

  interface Props {
    room: RoomListItemView;
    parentSpaceId?: string | null;
    open?: boolean;
    anchor?: HTMLElement | CursorAnchor | null;
    align?: 'start' | 'end';
    side?: 'bottom' | 'right';
    onSettings: (room: RoomListItemView) => void;
    onLeave: (room: RoomListItemView) => void;
    onLobby?: (room: RoomListItemView) => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onUnpin?: () => void;
    onRemoveFromFolder?: () => void;
  }

  let {
    room,
    parentSpaceId = null,
    open = $bindable(false),
    anchor = null,
    align = 'end',
    side = 'bottom',
    onSettings,
    onLeave,
    onLobby,
    onMoveUp,
    onMoveDown,
    onUnpin,
    onRemoveFromFolder,
  }: Props = $props();
  const core = useCoreClient();
  const roomList = useRoomList();

  // The core enriches tags once per room per subscription, so a toggle has to
  // hold its own answer until the next enrichment.
  const pendingTags = new SvelteMap<RoomTag, boolean>();

  let favourite = $derived(pendingTags.get('favourite') ?? room.tags.includes('favourite'));
  let parentSpace = $derived(roomList.byId(parentSpaceId) ?? null);
  let addableSpaces = $derived(
    roomList.rooms.filter(
      (candidate) =>
        candidate.room_kind === 'space' &&
        candidate.state === 'joined' &&
        candidate.room_id !== room.room_id &&
        !candidate.space_children.some((child) => child.room_id === room.room_id) &&
        !wouldCreateCycle(roomList.rooms, candidate.room_id, room.room_id)
    )
  );

  let readable = $derived(
    room.room_kind === 'space' ? spaceDescendantRooms(roomList.rooms, room.room_id) : [room]
  );
  let unread = $derived(
    readable.some((entry) => entry.unread > 0 || entry.highlight > 0 || entry.marked_unread)
  );
  let quiet = $derived(isQuiet(room.room_id));
  let quietBySpace = $derived(!quiet && roomList.quietRoomIds.has(room.room_id));

  const manageable = new SvelteSet<string>();
  let manageableRun = 0;

  function readManageableSpaces(): void {
    const candidates = [...addableSpaces.map((space) => space.room_id), parentSpaceId].filter(
      (id): id is string => id !== null
    );

    const run = ++manageableRun;
    manageable.clear();
    for (const spaceId of candidates) {
      void core.rpc.members
        .permissions({ room_id: spaceId })
        .then((permissions) => {
          if (run !== manageableRun || !permissions.can_manage_children) return;
          manageable.add(spaceId);
        })
        .catch((error: unknown) => {
          console.debug('[sable room] space permissions unavailable', error);
        });
    }
  }

  function readInvitePermission(): void {
    const run = ++inviteRun;
    canInvite = false;
    void core.rpc.members
      .permissions({ room_id: room.room_id })
      .then((permissions) => {
        if (run !== inviteRun) return;
        canInvite = permissions.can_invite;
      })
      .catch((error: unknown) => {
        console.debug('[sable room] room permissions unavailable', error);
      });
  }

  let offeredSpaces = $derived(addableSpaces.filter((space) => manageable.has(space.room_id)));
  let removableParent = $derived(
    parentSpace !== null && manageable.has(parentSpace.room_id) ? parentSpace : null
  );

  let joinableParents = $state.raw<{ room_id: string; via: string[]; name: string | null }[]>([]);
  let parentsRun = 0;

  function readParents(): void {
    const run = ++parentsRun;
    joinableParents = [];
    void core.rpc.spaces
      .unjoinedSpaceParents({ room_id: room.room_id })
      .then((parents) => {
        if (run !== parentsRun) return;
        joinableParents = parents.map((parent) => ({ ...parent, name: null }));
        for (const parent of parents) {
          void core.rpc.rooms
            .preview({ address: parent.room_id, via: parent.via })
            .then((preview) => {
              if (run !== parentsRun) return;
              joinableParents = joinableParents.map((entry) =>
                entry.room_id === parent.room_id ? { ...entry, name: preview.name } : entry
              );
            })
            .catch((error: unknown) => {
              console.debug('[sable room] parent space preview unavailable', error);
            });
        }
      })
      .catch((error: unknown) => {
        console.debug('[sable room] parent spaces unavailable', error);
      });
  }

  function joinParent(parent: { room_id: string; via: string[] }): void {
    void joinRoom(core.rpc, parent.room_id, parent.via).catch(report);
  }

  let opened = $state(false);
  let addToSpaceOpen = $state(false);
  let inviteOpen = $state(false);
  let canInvite = $state(false);
  let inviteRun = 0;

  $effect(() => {
    if (!open) return;
    opened = true;
    untrack(readManageableSpaces);
    untrack(readInvitePermission);
    untrack(readParents);
  });

  function report(error: unknown): void {
    console.warn('[sable room] room action failed', error);
    toasts.error(i18n.t('errors.actionFailed'));
  }

  function toggleTag(tag: RoomTag, current: boolean): void {
    const next = !current;
    pendingTags.set(tag, next);
    void core.rpc.rooms
      .setTag({ room_id: room.room_id, tag, set: next })
      .catch((error: unknown) => {
        pendingTags.delete(tag);
        report(error);
      });
  }

  /** One-directional, like v1: a DM becomes a group. */
  function convertToGroup(): void {
    void core.rpc.account
      .setDirect({ room_id: room.room_id, direct: false, user_id: null })
      .catch(report);
  }

  function addToSpaces(spaceIds: string[]): void {
    for (const spaceId of spaceIds) {
      void core.rpc.spaces
        .add({ space_id: spaceId, room_id: room.room_id, suggested: null })
        .catch(report);
    }
  }

  function removeFromSpace(spaceId: string): void {
    void core.rpc.spaces.remove({ space_id: spaceId, room_id: room.room_id }).catch(report);
  }

  function markRead(): void {
    markRoomsRead(readable, core.rpc.timeline, readReceiptIsPrivate());
  }

  function markUnread(): void {
    markRoomUnread(room.room_id, core.rpc.timeline);
  }

  async function copyLink(): Promise<void> {
    if (!(await copyRoomLink(core, room))) toasts.error(i18n.t('errors.copyFailed'));
  }

  async function copyId(): Promise<void> {
    try {
      await copyText(room.room_id);
    } catch (error) {
      console.debug('[sable room] copy id failed', error);
      toasts.error(i18n.t('errors.copyFailed'));
    }
  }
</script>

{#snippet optionsTrigger({ props }: { props: Record<string, unknown> })}
  <button
    {...props}
    type="button"
    class="room-options-trigger selection-open"
    aria-label={i18n.t('room.menuLabel')}
  >
    <DotsThreeVerticalIcon />
  </button>
{/snippet}

<ActionMenu
  bind:open
  label={i18n.t('room.menuLabel')}
  class="room-options-menu"
  {anchor}
  {side}
  {align}
  preventScroll={false}
  trigger={anchor ? undefined : optionsTrigger}
>
  <IconContext values={{ 'aria-hidden': 'true' }}>
    {#if room.room_kind === 'space' || unread}
      <ActionMenuItem disabled={!unread} onSelect={markRead}>
        <ChecksIcon />
        {i18n.t('common.markAsRead')}
      </ActionMenuItem>
    {:else}
      <ActionMenuItem onSelect={markUnread}>
        <CircleDashedIcon />
        {i18n.t('room.menuMarkUnread')}
      </ActionMenuItem>
    {/if}
    <ActionMenuSeparator />
    <ActionMenuItem
      onSelect={() => {
        toggleTag('favourite', favourite);
      }}
    >
      <StarIcon weight={favourite ? 'fill' : 'regular'} />
      {i18n.t('room.menuFavourite')}
    </ActionMenuItem>
    <ActionMenuItem
      disabled={quietBySpace}
      onSelect={() => {
        setQuiet(room.room_id, !quiet);
      }}
    >
      {#if quiet || quietBySpace}
        <EyeIcon />
      {:else}
        <EyeSlashIcon />
      {/if}
      {quietBySpace
        ? i18n.t('room.menuUnreadHiddenBySpace')
        : quiet
          ? i18n.t('room.menuShowUnread')
          : i18n.t('room.menuHideUnread')}
    </ActionMenuItem>

    {#if room.is_direct}
      <ActionMenuItem onSelect={convertToGroup}>
        <ChatCircleIcon />
        {i18n.t('room.menuConvertToGroup')}
      </ActionMenuItem>
    {/if}

    <ActionMenuSeparator />

    <ActionMenuItem
      disabled={!canInvite}
      onSelect={() => {
        inviteOpen = true;
      }}
    >
      <UserPlusIcon />
      {i18n.t('room.menuInvite')}
    </ActionMenuItem>
    {#if room.room_kind === 'space' && onLobby}
      <ActionMenuItem
        onSelect={() => {
          onLobby(room);
        }}
      >
        <FlagIcon />
        {i18n.t('nav.lobby')}
      </ActionMenuItem>
    {/if}
    {#if room.room_kind === 'space'}
      <ActionMenuItem
        onSelect={() => {
          // eslint-disable-next-line svelte/no-navigation-without-resolve -- spaceTimelinePath() resolves the route itself
          void goto(spaceTimelinePath(room, roomList.rooms));
        }}
      >
        <ListBulletsIcon />
        {i18n.t('room.menuShowSpaceTimeline')}
      </ActionMenuItem>
    {/if}
    <ActionMenuItem onSelect={copyLink}>
      <LinkIcon />
      {i18n.t('room.menuCopyLink')}
    </ActionMenuItem>
    {#if preferences.developerTools}
      <ActionMenuItem onSelect={copyId}>
        <FingerprintIcon />
        {i18n.t('room.menuCopyId')}
      </ActionMenuItem>
    {/if}
    <ActionMenuItem
      onSelect={() => {
        onSettings(room);
      }}
    >
      <GearIcon />
      {i18n.t('room.menuSettings')}
    </ActionMenuItem>

    {#if onMoveUp}
      <ActionMenuItem onSelect={onMoveUp}>
        <ArrowUpIcon />
        {i18n.t('room.menuMoveUp')}
      </ActionMenuItem>
    {/if}
    {#if onMoveDown}
      <ActionMenuItem onSelect={onMoveDown}>
        <ArrowDownIcon />
        {i18n.t('room.menuMoveDown')}
      </ActionMenuItem>
    {/if}
    {#if onUnpin}
      <ActionMenuItem onSelect={onUnpin}>
        <PushPinSlashIcon />
        {i18n.t('nav.unpinFromSidebar')}
      </ActionMenuItem>
    {/if}
    {#if onRemoveFromFolder}
      <ActionMenuItem onSelect={onRemoveFromFolder}>
        <ArrowLineUpIcon />
        {i18n.t('nav.folderRemoveSpace')}
      </ActionMenuItem>
    {/if}

    <RoomNotificationSubmenu roomId={room.room_id} active={opened} />

    {#if offeredSpaces.length > 0}
      <ActionMenuItem
        onSelect={() => {
          addToSpaceOpen = true;
        }}
      >
        <UsersThreeIcon />
        {i18n.t('room.createParentLabel')}
      </ActionMenuItem>
    {/if}

    {#each joinableParents as parent (parent.room_id)}
      <ActionMenuItem
        onSelect={() => {
          joinParent(parent);
        }}
      >
        <SignInIcon />
        {i18n.t('room.menuJoinParentSpace', { space: parent.name ?? parent.room_id })}
      </ActionMenuItem>
    {/each}

    {#if room.room_kind !== 'space' && removableParent}
      <ActionMenuItem
        onSelect={() => {
          removeFromSpace(removableParent.room_id);
        }}
      >
        <UsersThreeIcon />
        {i18n.t('room.menuRemoveFromSpace', {
          space: removableParent.name ?? removableParent.room_id,
        })}
      </ActionMenuItem>
    {/if}

    <ActionMenuItem
      destructive
      onSelect={() => {
        onLeave(room);
      }}
    >
      <SignOutIcon />
      {room.room_kind === 'space' ? i18n.t('room.menuLeaveSpace') : i18n.t('room.menuLeave')}
    </ActionMenuItem>
  </IconContext>
</ActionMenu>

<AddToSpaceDialog
  open={addToSpaceOpen}
  {room}
  spaces={offeredSpaces}
  onOpenChange={(next) => {
    addToSpaceOpen = next;
  }}
  onApply={addToSpaces}
/>

<RoomInviteDialog
  open={inviteOpen}
  {room}
  onOpenChange={(next) => {
    inviteOpen = next;
  }}
/>

<style>
  :global(.room-options-menu) {
    --menu-min-width: 12rem;
    --menu-max-height: var(--bits-floating-available-height, 100dvh);
  }

  :global(.room-options-trigger) {
    --target: 1.5rem;

    align-items: center;
    background: transparent;
    border: 0;
    border-radius: var(--radius);
    color: var(--surface-var-on-container);
    cursor: pointer;
    display: inline-flex;
    flex: none;
    height: var(--target);
    justify-content: center;
    padding: 0;
    position: relative;
    width: var(--target);
  }

  :global(.room-options-trigger)::after {
    border-radius: inherit;
    content: '';
    inset: calc((var(--target) - var(--target-hit)) / 2);
    position: absolute;
  }

  :global(.room-options-trigger:hover) {
    background: var(--surface-container-hover);
    color: var(--surface-on-container);
  }

  :global(.room-options-trigger:focus-visible) {
    outline: var(--focus-ring-width) solid var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
  }
</style>
